import { useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';

import { useTranslate } from '@i18n';
import { uiNodeBySmpIdAtom } from '@store';

import { useSwingGuideModel } from './useSwingGuideModel';

export const useSwingGuide = (smpId: number) => {
    const [selectedSmpId, setSelectedSmpId] = useState(smpId);
    const t = useTranslate();
    const uiNodesBySmpId = useAtomValue(uiNodeBySmpIdAtom);
    const model = useSwingGuideModel(selectedSmpId);

    const availableAxes = useMemo(
        () =>
            model?.availableGuideAxes.flatMap(({ id, parameters: [firstParameter] }) => {
                const firstParameterNode = firstParameter ? uiNodesBySmpId[firstParameter.smpId] : undefined;

                return firstParameterNode ? [{ value: String(id), label: t(firstParameterNode.i18n ?? '-') }] : [];
            }) ?? [],
        [model?.availableGuideAxes, t, uiNodesBySmpId],
    );
    const availableParameters = useMemo(
        () =>
            model?.selectedGuideAxis.parameters.map(({ smpId: id, pPosition }) => ({
                value: String(id),
                label: `P${pPosition}`,
            })) ?? [],
        [model?.selectedGuideAxis.parameters],
    );

    if (!model) return null;

    const { selectedParameterNode, guide, selectedGuideAxis, selectedGuideParameter, availableGuideAxes } = model;

    const handleAxisChange = (axisId: string) => {
        const nextSmpId = availableGuideAxes.find(({ id }) => String(id) === axisId)?.parameters[0]?.smpId;

        if (nextSmpId) setSelectedSmpId(nextSmpId);
    };

    return {
        selectedSmpId,
        setSelectedSmpId,
        selectedParameterNode,
        guide,
        selectedGuideAxis,
        selectedGuideParameter,
        availableAxes,
        availableParameters,
        handleAxisChange,
    };
};
